// REST auth client for the admin — talks to the loginSam backend endpoints
// (same auth path as the mobile apps) instead of AWS Amplify / Cognito directly.
// The backend resolves the correct per-tenant Cognito pool.

import {
  storeTokens,
  getStoredRefreshToken,
  getStoredAccessToken,
} from "../utils/token";

const API = import.meta.env.VITE_API_BASE_URL;
const API_KEY =
  import.meta.env.VITE_API_KEY;

async function post(path: string, body: any, tenantId?: string | null) {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Api-Key": API_KEY,
  };
  // Send the tenant in the body, not as X-Tenant-Id. The header is not a
  // CORS-safelisted one, so it makes the browser send a preflight, and the
  // OPTIONS response on /login only allows
  // Content-Type, Authorization, X-Amz-Date, X-Api-Key, X-Amz-Security-Token.
  // The preflight fails and every login dies as a CORS error. The mobile apps
  // never hit this because native HTTP clients do not preflight.
  // loginSam reads the tenant from the header, the query string OR the body
  // (_explicit_tenant), so the body carries it just as well and needs no
  // change to the API Gateway CORS configuration.
  const payload = tenantId ? { ...body, tenant_id: tenantId } : body;
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  let data: any = {};
  try {
    data = await res.json();
  } catch {
    /* empty body */
  }
  return { ok: res.ok, status: res.status, data };
}

export type LoginResult =
  | { result: "SUCCESS" }
  | { result: "NEW_PASSWORD_REQUIRED"; session: string }
  | { result: "ERROR"; message: string };

// Tenant to announce to the backend (sent in the request body — see post()).
//
// selectedTenantId is only written once useTenant has a token to read the
// tenant out of, so on the login screen it is always null and the request went
// out with no tenant at all. The backend then falls back to resolving the pool
// from the email, which returns the first pool in the tenant map holding that
// address — wrong for anyone who belongs to more than one community.
//
// useTenantBranding already resolves the community from the domain to paint the
// login screen and caches it under tenant_branding_<domain>, so fall back to
// that before giving up. Login.tsx also passes the id explicitly, which does not
// depend on the cache having been written yet.
function tenantHint(): string | null {
  const selected = sessionStorage.getItem("selectedTenantId");
  if (selected) return selected;
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key?.startsWith("tenant_branding_")) continue;
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
      const id = JSON.parse(raw)?.id;
      if (id) return String(id);
    }
  } catch {
    /* unreadable or malformed cache — fall through to no hint */
  }
  return null;
}

export async function login(
  email: string,
  password: string,
  tenantId: string | null = tenantHint()
): Promise<LoginResult> {
  const { ok, data } = await post("/login", { username: email, password }, tenantId);
  if (ok && data?.status === "SUCCESS" && data.tokens) {
    storeTokens(data.tokens);
    return { result: "SUCCESS" };
  }
  if (ok && data?.status === "PASSWORD_CHANGE_REQUIRED") {
    return { result: "NEW_PASSWORD_REQUIRED", session: data.session };
  }
  return {
    result: "ERROR",
    message: data?.error || data?.message || "Login failed",
  };
}

export async function confirmNewPassword(
  email: string,
  password: string,
  newPassword: string,
  session: string,
  tenantId: string | null = tenantHint()
): Promise<LoginResult> {
  const { ok, data } = await post(
    "/login",
    { username: email, password, new_password: newPassword, session },
    tenantId
  );
  if (
    ok &&
    (data?.status === "PASSWORD_CHANGED" || data?.status === "SUCCESS") &&
    data.tokens
  ) {
    storeTokens(data.tokens);
    return { result: "SUCCESS" };
  }
  return {
    result: "ERROR",
    message: data?.error || data?.message || "Failed to set new password",
  };
}

// SAM-1298 (W2): a business owner creates the account from «Register your business». Same pool and same loginSam
// routes as the Merchants app (POST /login/signup, /login/confirm-signup, /login/resend-code); the server sets the
// tenant from the pool. Errors carry loginSam's `code` (USER_EXISTS, INVALID_CODE, EXPIRED_CODE, ...).
export class SignUpError extends Error {
  constructor(message: string, public code: string | null) {
    super(message);
  }
}

const signUpCall = async (path: string, body: Record<string, unknown>, tenantId: string | null) => {
  const { ok, data } = await post(path, body, tenantId);
  if (!ok) throw new SignUpError(data?.error || data?.message || "Something went wrong", data?.code ?? null);
  return data;
};

export const signUp = (email: string, password: string, tenantId: string | null = tenantHint()) =>
  signUpCall("/login/signup", { email, password }, tenantId);

export const confirmSignUp = (email: string, code: string, tenantId: string | null = tenantHint()) =>
  signUpCall("/login/confirm-signup", { email, confirmation_code: code }, tenantId);

export const resendSignUpCode = (email: string, tenantId: string | null = tenantHint()) =>
  signUpCall("/login/resend-code", { email }, tenantId);

export async function forgotPassword(email: string) {
  const { ok, data } = await post("/login/forgot-password", { email });
  if (!ok)
    throw new Error(data?.error || data?.message || "Failed to start password reset");
  return data;
}

export async function confirmResetPassword(
  email: string,
  code: string,
  newPassword: string
) {
  const { ok, data } = await post("/login/confirm-forgot-password", {
    email,
    confirmation_code: code,
    new_password: newPassword,
  });
  if (!ok)
    throw new Error(data?.error || data?.message || "Failed to reset password");
  return data;
}

export async function refreshToken(
  tenantId: string | null = tenantHint()
): Promise<boolean> {
  const rt = getStoredRefreshToken();
  // A session with no refresh token, or one the server turns down (400/401/403 is its answer; 5xx, 429 and a
  // dropped connection are not), is over: callers read `rejected` to end it instead of retrying forever.
  if (!rt) throw Object.assign(new Error("No refresh token"), { rejected: true });
  const { ok, status, data } = await post("/login/refresh", { refresh_token: rt }, tenantId);
  // loginSam answers {status, tokens: {id_token, access_token, ...}} since SAM-459;
  // the Cognito-shaped AuthenticationResult is the older form. Reading only the
  // old one made every successful refresh look failed, so sessions ended at the
  // id token's one-hour expiry (SAM-766).
  const tokens = data?.tokens ?? (data?.AuthenticationResult && {
    id_token: data.AuthenticationResult.IdToken,
    access_token: data.AuthenticationResult.AccessToken,
  });
  if (ok && tokens?.id_token) {
    // refresh token is not re-issued on refresh; keep the existing one
    storeTokens({ id_token: tokens.id_token, access_token: tokens.access_token });
    return true;
  }
  throw Object.assign(new Error(data?.error || data?.message || "Refresh failed"), {
    rejected: !ok && (status === 400 || status === 401 || status === 403),
  });
}

export async function logout(tenantId: string | null = tenantHint()) {
  const at = getStoredAccessToken();
  if (at) await post("/login/logout", { access_token: at }, tenantId).catch(() => {});
}
